import React, { useMemo, useState } from 'react';
import { Heart, Copy, Wand2, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import FlagIcon from './FlagIcon';
import LanguageListPopover from './LanguageListPopover';
import DarkTooltip from '../ui/DarkTooltip';
import {
  getPrimaryFlagCode,
  getCountryName,
  uniqueLanguages,
  translate,
  GENDER_PT,
  AGE_PT,
  CATEGORY_PT,
  ACCENT_PT,
  USE_CASE_PT,
  LANGUAGE_PT,
} from '../../utils/voiceMetadata';

/**
 * VoiceCardCompact — card pra lista lateral.
 *
 * Substitui visualmente o `renderVoiceVisual` original (que continua existindo
 * pra ser usado pelo modal de swap multivoz). Comportamento esperado:
 *   - Click no card → onSelect(voice)
 *   - Click no avatar → onTogglePreview(voice)  (se voice.preview_url)
 *   - Click no heart → onToggleFavorite(voice)
 *   - Click no copy slug → copia voice.slug
 *   - Click na varinha → onCreateVariation(voice)  (salva em Minhas Vozes com outro nome)
 *
 * Modo VARIAÇÃO (voice.is_variation): card mostra o nome customizado em cima e
 * o nome ORIGINAL da voz embaixo; ações viram [✏ editar] + [🗑 excluir].
 *
 * Layout:
 *   [Avatar 30] Nome  [MULTIVOZ?]  [HQ?]
 *               [Gênero PT] · [🇺🇸 +N idiomas] · [Provider]
 *   [🪄][♡] top-right (ocultos até hover, exceto ♡ se já favorito)
 *   [📋 slug] abaixo do ♡ (oculto até hover)
 */
export default function VoiceCardCompact({
  voice,
  isSelected = false,
  isFavorite = false,
  isPreviewing = false,
  isMultivoz = false,
  onSelect,
  onTogglePreview,
  onToggleFavorite,
  onCreateVariation,
  onEditVariation,
  onDeleteVariation,
  avatar, // ReactNode — consumer injeta o <VoiceAvatar>
}) {
  const { t } = useTranslation();
  const isVariation = !!voice.is_variation;
  const info = voice.voice_info;
  const isCapCut = (voice.provider || '').toLowerCase() === 'capcut';
  // FASE 2.2: popover de idiomas substitui o title HTML nativo do "+N"
  const [popoverAnchor, setPopoverAnchor] = useState(null);

  // Bandeira primária (com locale do verified_languages match) + count de others
  const flagData = useMemo(() => {
    if (!info) return null;
    const primaryCode = getPrimaryFlagCode(voice);
    const unique = uniqueLanguages(info.verified_languages);
    const primaryLang = (info.language || '').toLowerCase();
    const primaryAccent = (info.accent || '').toLowerCase();
    const others = unique.filter((e) => {
      const l = (e.language || '').toLowerCase();
      const a = (e.accent || '').toLowerCase();
      return l !== primaryLang || a !== primaryAccent;
    });
    return {
      primaryCode,
      othersCount: others.length,
    };
  }, [info, voice]);

  // Gênero: SÓ voice.gender top-level (fonte: Carlos). O voice_info.gender do
  // ElevenLabs tem dados sujos em parte do catálogo (ex.: "The Pharaoh 4" listada
  // como female no Carlos mas male no voice_info upstream).
  const genderRaw = voice.gender || '';
  const genderPt = translate(genderRaw, GENDER_PT) || (genderRaw === 'male' ? 'Masculino' : genderRaw === 'female' ? 'Feminino' : '');

  // Conteúdo do rich tooltip (memo — só recalcula se voice mudar)
  const richTooltipContent = useMemo(() => {
    const langPt = info && info.language ? translate(info.language, LANGUAGE_PT) : '';
    const agePt = info && info.age ? translate(info.age, AGE_PT) : '';
    const description = info && info.description ? String(info.description) : '';
    const tags = [];
    if (info && info.category) tags.push(translate(info.category, CATEGORY_PT));
    if (info && info.accent && info.accent.toLowerCase() !== 'standard') tags.push(translate(info.accent, ACCENT_PT));
    if (info && info.use_case) tags.push(translate(info.use_case, USE_CASE_PT));
    const tagsTrimmed = tags.filter(Boolean).slice(0, 3);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--dp-text-primary)' }}>
          {voice.name}
        </div>
        <div style={{
          display: 'flex',
          gap: 6,
          fontSize: 11,
          color: 'var(--dp-text-secondary)',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}>
          {flagData && flagData.primaryCode && (
            <FlagIcon code={flagData.primaryCode} size="14px" rounded />
          )}
          {langPt && <span>{langPt}</span>}
          {genderPt && <><span style={{ opacity: 0.4 }}>·</span><span>{genderPt}</span></>}
          {agePt && <><span style={{ opacity: 0.4 }}>·</span><span>{agePt}</span></>}
        </div>
        {tagsTrimmed.length > 0 && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
            {tagsTrimmed.map((t, i) => (
              <span key={i} style={{
                fontSize: 10,
                padding: '2px 6px',
                borderRadius: 4,
                background: 'rgba(13, 174, 255, 0.10)',
                color: '#06B6D4',
                whiteSpace: 'nowrap',
              }}>{t}</span>
            ))}
          </div>
        )}
        {description && (
          <div style={{
            fontSize: 11,
            color: 'var(--dp-text-tertiary)',
            lineHeight: 1.4,
            marginTop: 4,
            fontStyle: 'italic',
          }}>
            “{description.length > 120 ? description.substring(0, 120) + '…' : description}”
          </div>
        )}
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voice, info, flagData, genderPt]);

  const baseBackground = isSelected
    ? 'linear-gradient(135deg, rgba(13,174,255,0.08), rgba(13,174,255,0.03))'
    : (isMultivoz ? 'rgba(13,174,255,0.04)' : 'transparent');

  return (
    <div
      className="voice-item-ag"
      onClick={() => onSelect && onSelect(voice)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 10px',
        borderRadius: 'var(--dp-radius-sm, 8px)',
        cursor: 'pointer',
        transition: 'all 0.2s',
        marginBottom: '1px',
        border: isSelected ? '1px solid rgba(13,174,255,0.15)' : '1px solid transparent',
        borderLeft: isMultivoz
          ? '3px solid var(--dp-blue-500)'
          : (isSelected ? '1px solid rgba(13,174,255,0.15)' : '1px solid transparent'),
        background: baseBackground,
        position: 'relative',
      }}
      onMouseEnter={(e) => {
        if (!isSelected) e.currentTarget.style.background = isMultivoz ? 'rgba(13,174,255,0.08)' : 'rgba(13, 174, 255, 0.04)';
      }}
      onMouseLeave={(e) => {
        if (!isSelected) e.currentTarget.style.background = baseBackground;
      }}
    >
      {/* Avatar (injetado) — handler de preview vem no próprio componente.
          Variação ganha badge azul (varinha) no canto do avatar. */}
      <div style={{ position: 'relative', flexShrink: 0, display: 'flex' }}>
        {avatar}
        {isVariation && (
          <span style={{
            position: 'absolute',
            bottom: '-2px',
            left: '-3px',
            width: '13px',
            height: '13px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1.5px solid var(--dp-bg-card, #1a1a2e)',
            pointerEvents: 'none',
          }}>
            <Wand2 size={7} color="#FFFFFF" strokeWidth={2.5} />
          </span>
        )}
      </div>

      {/* Nome + meta — rich tooltip aplicado SÓ no <span> do nome (FIX 2) */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Linha 1: nome + badges inline */}
        <div style={{
          fontSize: '12px',
          fontWeight: isSelected ? 500 : 400,
          color: isSelected ? 'var(--dp-blue-500)' : 'var(--dp-text-primary)',
          lineHeight: '1.3',
          marginBottom: '2px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}>
          <DarkTooltip content={richTooltipContent} delay={900} position="top" maxWidth={320}>
            <span style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              wordBreak: 'break-word',
              lineHeight: 1.25,
            }}>{voice.name}</span>
          </DarkTooltip>

          {voice.name === 'Multivoz' && (
            <span style={{
              fontSize: '8.5px',
              fontWeight: 700,
              color: 'var(--dp-blue-500)',
              background: 'rgba(13,174,255,0.14)',
              border: '1px solid rgba(13,174,255,0.35)',
              padding: '1px 5px',
              borderRadius: '9999px',
              letterSpacing: '0.4px',
              flexShrink: 0,
              lineHeight: 1.2,
            }}>MULTIVOZ</span>
          )}

          {isCapCut && (
            <span style={{
              fontSize: '8px',
              fontWeight: 700,
              color: '#F59E0B',
              background: 'rgba(245,158,11,0.10)',
              border: '1px solid rgba(245,158,11,0.30)',
              padding: '1px 5px',
              borderRadius: '4px',
              letterSpacing: '0.4px',
              flexShrink: 0,
              lineHeight: 1.2,
            }}>CapCut</span>
          )}
        </div>

        {/* Linha 2 (variação): nome ORIGINAL da voz */}
        {isVariation && (
          <div style={{
            fontSize: '10px',
            color: 'var(--dp-text-tertiary)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
          }}>
            {voice.original_name || ''}
          </div>
        )}

        {/* Linha 2: gênero · bandeira · provider */}
        {!isVariation && (
        <div style={{
          fontSize: '10px',
          color: 'var(--dp-text-tertiary)',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          minWidth: 0,
        }}>
          {genderPt && <span style={{ whiteSpace: 'nowrap' }}>{genderPt}</span>}

          {flagData && flagData.primaryCode && (
            <>
              {genderPt && <span style={{ opacity: 0.4 }}>·</span>}
              <span
                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', flexShrink: 0, minWidth: 0 }}
              >
                <DarkTooltip content={getCountryName(flagData.primaryCode)} delay={300}>
                  <FlagIcon code={flagData.primaryCode} size="13px" rounded />
                </DarkTooltip>
                {info && info.language && (
                  <span style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>{translate(info.language, LANGUAGE_PT)}</span>
                )}
                {flagData.othersCount > 0 && (
                  <DarkTooltip
                    content={`${flagData.othersCount} ${flagData.othersCount === 1 ? 'idioma adicional' : 'idiomas adicionais'} — clique pra ver`}
                    delay={300}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        setPopoverAnchor(rect);
                      }}
                      style={{
                        fontSize: '9px',
                        fontWeight: 600,
                        color: 'var(--dp-blue-500)',
                        background: 'rgba(13,174,255,0.10)',
                        padding: '0 4px',
                        borderRadius: '6px',
                        lineHeight: 1.4,
                        border: '1px solid transparent',
                        cursor: 'pointer',
                        fontFamily: 'var(--dp-font)',
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(13,174,255,0.18)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(13,174,255,0.10)'; }}
                    >+{flagData.othersCount}</button>
                  </DarkTooltip>
                )}
              </span>
            </>
          )}

          {voice.provider && (
            <>
              <span style={{ opacity: 0.4 }}>·</span>
              <span style={{
                color: 'var(--dp-blue-500)',
                opacity: 0.7,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}>{voice.provider}</span>
            </>
          )}
        </div>
        )}
      </div>

      {isVariation ? (
        /* Ações da variação — editar (✏) + excluir (🗑), hover via .fav-btn-ag */
        <div style={{
          position: 'absolute',
          top: '50%',
          right: '6px',
          transform: 'translateY(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
        }}>
          <DarkTooltip content={t('audio.variation_edit_title')} delay={300}>
            <button
              className="fav-btn-ag"
              onClick={(e) => { e.stopPropagation(); onEditVariation && onEditVariation(voice); }}
              style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                border: 'none',
                background: 'transparent',
                color: 'var(--dp-text-secondary)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0,
                transition: 'all 0.2s',
                padding: 0,
              }}
            >
              <Pencil size={11} />
            </button>
          </DarkTooltip>
          <DarkTooltip content={t('audio.variation_delete')} delay={300}>
            <button
              className="fav-btn-ag"
              onClick={(e) => { e.stopPropagation(); onDeleteVariation && onDeleteVariation(voice); }}
              style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                border: 'none',
                background: 'transparent',
                color: '#F87171',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0,
                transition: 'all 0.2s',
                padding: 0,
              }}
            >
              <Trash2 size={11} />
            </button>
          </DarkTooltip>
        </div>
      ) : (
        <>
          {/* Criar variação (🪄) — à esquerda do coração, aparece em hover */}
          {onCreateVariation && (
            <div style={{ position: 'absolute', top: '4px', right: '24px' }}>
              <DarkTooltip content={t('audio.variation_create_tooltip')} delay={300}>
                <button
                  className="fav-btn-ag"
                  onClick={(e) => { e.stopPropagation(); onCreateVariation(voice); }}
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--dp-blue-500)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'all 0.2s',
                    padding: 0,
                  }}
                >
                  <Wand2 size={11} />
                </button>
              </DarkTooltip>
            </div>
          )}

          {/* Favorite heart — top-right */}
          <button
            className="fav-btn-ag"
            onClick={(e) => { e.stopPropagation(); onToggleFavorite && onToggleFavorite(voice); }}
            style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              border: 'none',
              background: 'transparent',
              color: isFavorite ? '#F87171' : 'var(--dp-text-tertiary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: isFavorite ? 1 : 0,
              transition: 'all 0.2s',
              padding: 0,
            }}
          >
            <Heart size={10} fill={isFavorite ? '#F87171' : 'none'} />
          </button>

          {/* Copy slug — abaixo do coração, aparece em hover via .fav-btn-ag */}
          {voice.slug && (
            <button
              className="fav-btn-ag"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(voice.slug);
                toast.success(`Slug copiado: ${voice.slug}`);
              }}
              title={`Copiar slug: ${voice.slug}`}
              style={{
                position: 'absolute',
                top: '26px',
                right: '4px',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                border: 'none',
                background: 'transparent',
                color: 'var(--dp-text-tertiary)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0,
                transition: 'all 0.2s',
                padding: 0,
              }}
            >
              <Copy size={10} />
            </button>
          )}
        </>
      )}

      {/* Popover de idiomas — fica em portal, posicionado pelo anchorRect */}
      {popoverAnchor && (
        <LanguageListPopover
          voice={voice}
          anchorRect={popoverAnchor}
          onClose={() => setPopoverAnchor(null)}
        />
      )}
    </div>
  );
}
